import { Fragment, useState, useEffect } from 'react';
import { connect } from 'react-redux';
import CustomizedMenus from 'components/common/CustomSelect/index';
import { makeStyles, ThemeProvider, createTheme } from '@material-ui/core/styles';
import { MenuItem, ListItemText, Paper, InputBase } from '@material-ui/core';
import { CheckBoxOutlined, CheckBoxOutlineBlankOutlined } from '@material-ui/icons';
import { Button as CustomButton, BUTTON } from 'components/common/Button/Button';
import styles from './MultiSelectCheckbox.scss';
import { filter, findIndex, get, isArray, isEmpty, some } from 'lodash';
import DateTimePicker from 'common/components/DateTime/DateTimePicker';
import FlatInput from 'components/common/Input/FlatInput';
import { getRawValue, getStringValue } from 'common/util/dataTypeValues';
import { dataSourceLogActions, dataSourceStatus } from 'pages/dataSource/utils/constants';

import {
    loadDataSourceList,
    loadDataSource,
    unloadDataSource,
    saveDataSource,
    writeDataSourceValues,
    loadDataSourceLog
} from 'pages/dataSource/actions/dataSource';
import { formatDate } from '../../../overview/containers/BindersV4/HelperFunctions/formatDate';

const ACTIVE_DATA_SOURCE_STATUS = 'active';
const useStyles = makeStyles((theme) => ({
    wrapper: {
        paddingTop: 10
    },
    formControl: {
        width: '100%',
    },
    Select: {
        '&:after': {
            borderBottom: 'none'
        }
    },
    header: {
        color: '#28282E',
        fontFamily: 'Hind',
        fontSize: '1rem',
        fontStyle: 'normal',
        fontWeight: 500,
        lineHeight: '20px',
        paddingBottom: '5px',
    },
    required: {
        fontSize: '1rem',
        fontWeight: '500',
        color: '#ff923d',
        marginLeft: '0.57143rem'
    },
    inputBase: {
        width: '100%',
        height: '40px',
        borderRadius: '4px',
        border: '1px solid #CCC !important',
        padding: '10px',
        minWidth: 120,
        '&:active, &:focus, &:hover': {
            borderColor: '#2057AD !important'
        },
        '&:before': {
            display: 'none',
        },
        '&:after': {
            display: 'none',
        },
        '& > div': {
            padding: '0px',
            color: '#28282E',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            fontFamily: 'Hind',
            fontSize: '16px',
            fontWeight: 400,
            lineHeight: '28px',
        },
        '& > div:focus': {
            backgroundColor: 'initial',
        },
    },

    errorInput: {
        width: '100%',
        borderRadius: '3px',
        border: '1px solid var(--red-500, #C33) !important',
        padding: '6px',
        minWidth: 120,
        '&:before': {
            display: 'none',
        },
        '&:after': {
            display: 'none',
        },
        '& > div': {
            padding: '0px',
            color: '#28282E',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            fontFamily: 'Hind',
            fontSize: '16px',
            fontWeight: 400,
            lineHeight: 'normal',
        },
        '& > div:focus': {
            backgroundColor: 'initial',
        },
    },
    subHeader: {
        backgroundColor: '#2057AD',
        color: '#fff',
        padding: '0rem 0.5rem',
        height: '40px',
        fontWeight: 600,
        lineHeight: '20px',
        display: 'flex',
        alignItems: 'center',
    },
    MenuItems: {
        height: '40px',
        padding: '0px 20px',
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        fontFamily: 'Hind',
        fontStyle: 'normal',
        fontWeight: 400,
        lineHeight: '20px',
        color: '#404040',
        backgroundColor: '#fff !important'
    },
    disabledItem: {
        display: 'none'
    },
    NotFocusable: {
        '&:after': {
            borderBottom: 'none'
        }
    },
    Required: {
        color: '#F1913B',
    },
    CheckBoxes: {
        width: '1rem',
        height: '1rem',
        color: 'var(--blue-500, #2057AD)'
    },
    ListItemText: {
        '& > span': {
            color: '#404040',
            fontSize: 16,
            fontFamily: 'Hind',
            fontWeight: '400',
            lineHeight: 20,
            wordWrap: 'break-word'
        }
    },
    ListSubheader: {
        background: '#2057AD',
        color: '#FFF',
        fontFamily: 'Hind',
        fontSize: '16px',
        fontStyle: 'normal',
        fontWeight: 600
    },
    Paper: {
        width: '600px',
        marginTop: '12px',
        marginLeft: '-10px',
        maxHeight: '400px',
    }
}));

const MULTI_SELECT_TYPE = 'multi-select';
const DATE_TYPE = 'date';
const ERROR_MESSAGE = `A Data Source with this name already exists.
Please select that Data Source or give the new Data Source a different name.`;

function BinderV4Field(props) {
    const {
        label,
        type,
        placeholder,
        canAddDataSource,
        showCreateField,
        options = [],
        isRequired,
        isEmptyError,
        onSelectedOption,
        Tooltip,
        chipData = [],
        isStepOne = false,
        shouldBeAllValuesSelected = false,
        instructions = 'Select a value'
    } = props;
    const classes = useStyles();
    const [selectedValues, setSelectedValues] = useState([]);
    const [isDatasourceDuplicated, setIsDatasourceDuplicated] = useState(false);
    const [isCreatingDataSource, setIsCreatingDataSource] = useState(false);

    useEffect(() => {
        if (!isEmpty(chipData)) {
            setSelectedValues(chipData);
        }

        if (shouldBeAllValuesSelected && (chipData.length !== options.length)) {
            handleChange(options);
        }
    }, []);

    // in binder create step one the data is fetch asynchronously so the component most rerender a few times
    useEffect(() => {
        if (!isEmpty(chipData) && isStepOne) {
            setSelectedValues(chipData);
        }
    }, [chipData]);
    const theme = createTheme({
        props: {
            MuiButtonBase: {
                disableRipple: true,
            },
        },
    });

    function getSelector(field) {
        return field.text || field.value || field.name || field.label;
    }

    const ThemedMenuItem = (props) => (
        <ThemeProvider theme={theme}>
            <MenuItem {...props}>{props.children}</MenuItem>
        </ThemeProvider>
    );

    const CheckboxIcon = ({ option, options }) => {
        if (!isArray(options)) return null;
        const isChecked = options.some(val => getSelector(val) === getSelector(option));

        return isChecked ? <CheckBoxOutlined className={classes.CheckBoxes} /> : <CheckBoxOutlineBlankOutlined className={classes.CheckBoxes} />;
    };

    function optionsSelected(option) {
        if (shouldBeAllValuesSelected) return;
        let values = [...selectedValues];

        const valueExists = values.some(val => getSelector(val) === getSelector(option));

        if (valueExists) {
            values = values.filter(val => getSelector(val) !== getSelector(option));
        } else {
            values.push(option);
        }

        handleChange(values);
    }

    function handleChange(value) {
        setSelectedValues(value);
        onSelectedOption(value);
    }

    function isMulti() {
        if (!type) return;
        return type === MULTI_SELECT_TYPE;
    }

    function isDate() {
        if (!type) return;
        return type.includes(DATE_TYPE);
    }

    function isPlainInput() {
        return !type;
    }

    const handleCreateDataSource = async (addNewValue, callbackFn) => {
        if (!addNewValue.trim()) return;

        const { datasourceId = null, institutionId: defaultInstitutionId } = props;
        if (!datasourceId) return;
        setIsCreatingDataSource(true);

        const dataSource = await props.loadDataSource(datasourceId, defaultInstitutionId);
        const dataSourceValues = dataSource && dataSource.parameters && dataSource.parameters.values;

        const dataType = dataSource.parameters.dataType;

        const value = determineValueForDataType(addNewValue, dataType);
        if (valueIsDuplicated(value, dataSourceValues)) return;

        const dataSourceItem = {
            value,
            text: getStringValue(dataType, addNewValue),
            status: dataSourceStatus.active.value,
            order: dataSourceValues.length + 1,
            dataType,
        };
        const newDataSourceValues = [...dataSourceValues, dataSourceItem];
        handleSaveDataSource(dataSourceLogActions.created, dataSourceItem, newDataSourceValues, dataSource, callbackFn);
    };

    const valueIsDuplicated = (value, dataSourceValues) => {
        const isDuplicated = some(dataSourceValues, { value });
        setIsDatasourceDuplicated(isDuplicated);
        return isDuplicated;
    };

    const determineValueForDataType = (value, dataType) => {
        const rawTypesNotAllowed = ['DATEONLY', 'DATETIME'];
        if (rawTypesNotAllowed.includes(dataType)) {
            return getStringValue(dataType, value);
        }
        return getRawValue(dataType, value);
    };

    const handleSaveDataSource = (action, dataSourceItem, dataSourceValues, dataSource, callbackFn) => {
        const { id: dataSourceMetadataId, institutionId, name, type } = dataSource;
        const showToaster = false;
        const dataSourceLog = {
            ...dataSourceItem,
            value: String(dataSourceItem.value),
            action,
            dataSourceMetadataId,
            institutionId,
            name,
            description: name,
            type,
        };

        props.loadDataSourceLog(dataSourceLog);
        props.writeDataSourceValues(dataSourceValues);
        props.saveDataSource(institutionId, null, showToaster).then(error => {
            props.loadDataSourceList(institutionId);
            if ((!isMulti() && !error)) {
                handleChange(dataSourceItem.text);
                callbackFn();
            }

            if (shouldBeAllValuesSelected && !error) {
                handleChange(dataSourceValues);
            }
            setIsCreatingDataSource(false);
        });
    };

    const className = isDatasourceDuplicated
        ? `${styles.input} ${styles.invalidInput}` // Combine classes with string concatenation
        : styles.input;

    function CreateNewDataSourceInputWithButton(props) {
        return (
            <Fragment>
                {(canAddDataSource && showCreateField) && (
                    <Fragment>
                        <Paper square elevation={0} className={styles.CreateSection}>
                            <InputBase
                                className={className}
                                disabled={isCreatingDataSource}
                                onChange={props.handleNewDataSource}
                                onKeyPress={(e) => e.key === 'Enter' && props.handleOnClick()}
                                value={props.newDataSource}
                                placeholder='New Data Source'
                                inputProps={{
                                    'aria-label': 'create data source', style: {
                                        fontSize: '16px', fontFamily: 'Hind',
                                        fontWeight: '400',
                                        fontStyle: 'normal',
                                        lineHeight: '18px',
                                        padding: '0.5px'
                                    }
                                }}
                            />
                            <CustomButton
                                bgcolor={BUTTON.BGCOLOR.BLUE}
                                color={BUTTON.COLOR.WHITE}
                                onClick={props.handleOnClick}
                                disabled={isCreatingDataSource}
                            >Create</CustomButton>
                        </Paper>
                        {isDatasourceDuplicated && (<pre className={styles.errorMessage}>{ERROR_MESSAGE}</pre>)}
                    </Fragment>
                )}
            </Fragment>
        );
    }

    function handleOnListOpen() {
        const { loadDataSource, datasourceId, institutionId } = props;
        if (!datasourceId) return;
        loadDataSource(datasourceId, institutionId);
    }

    function getActiveOptions(options) {
        if (!options) return [];
        // datasource objects only have active status, if not a datasource return all
        if (findIndex(options, option => option.status) === -1) return options;
        return filter(options, option => option.status === ACTIVE_DATA_SOURCE_STATUS);
    }

    function getSubLabel(field) {
        if (shouldBeAllValuesSelected) {
            return `All ${field} values will be included`;
        }

        return instructions;
    }

    return (
        <Fragment>
            {
                isDate() && (
                    <DatePicker {...props} />
                )
            }
            {
                isPlainInput() && (
                    <FlatInput {...props} />
                )
            }
            {
                !isDate() && !isPlainInput() && (
                    <CustomizedMenus
                        value={isArray(props.value) ? '' : props.value}
                        label={label}
                        placeholder={placeholder}
                        options={getActiveOptions(options)}
                        isRequired={isRequired}
                        onClose={() => unloadDataSource()}
                        notValid={isEmptyError}
                        onListOpened={handleOnListOpen}
                        Tooltip={Tooltip}
                        isSearchable={true}
                        onButtonClicked={(val, fn) => handleCreateDataSource(val, fn)}
                        subLabel={getSubLabel(label)}
                        chipData={shouldBeAllValuesSelected ? [] : selectedValues}
                        showInput={!shouldBeAllValuesSelected}
                        onSelectedOption={(value) => onSelectedOption(value)}
                        onChipLabelRemoved={handleChange}
                        listWidth={props.listWidth}
                        isReadOnly={props.isReadOnly}
                        renderChild={(props) => (
                            <section>
                                {
                                    props.filteredOptions.length === 0 && (
                                        <div className={styles.ListItems}>No available options</div>
                                    )
                                }
                                {
                                    isMulti() && <Fragment>
                                        {
                                            Array.isArray(props.filteredOptions) && props.filteredOptions.map((option, index) => (
                                                <div key={`${option.id}_${index}`}>
                                                    <ThemedMenuItem onClick={() => optionsSelected(option)} className={classes.MenuItems} value={option.value}>
                                                        <CheckboxIcon option={option} options={selectedValues} />
                                                        <ListItemText className={classes.ListItemText} primary={getSelector(option)} />
                                                    </ThemedMenuItem>
                                                </div>
                                            ))
                                        }
                                        <CreateNewDataSourceInputWithButton {...props} />
                                    </Fragment>
                                } {
                                    !isMulti() && <Fragment>
                                        {
                                            Array.isArray(props.filteredOptions) && (
                                                <ul>
                                                    {
                                                        props.filteredOptions.map((el, index) => (
                                                            <li className={styles.ListItems} style={{ backgroundColor: `${props.isOptionSelected(el) && '#2684FF'}`, color: `${props.isOptionSelected(el) && '#FFF'}` }} onClick={() => props.handleOptionSelected(el)} key={`${index * Math.random()}_${el.label}}`}>{el.text || el.label}</li>
                                                        ))
                                                    }
                                                </ul>
                                            )
                                        }
                                        <CreateNewDataSourceInputWithButton {...props} />
                                    </Fragment>
                                }
                            </section>
                        )}
                    />
                )
            }
        </Fragment>
    );
}

function DatePicker({ label, isRequired, Tooltip, onSelectedOption, ...props }) {

    const [value, setValue] = useState(props.value);
    function handleChange(value) {
        setValue(value);
        if (onSelectedOption) {
            onSelectedOption(value);
        }
    }
    let content = '-';
    if (!props.isReadOnly) {
        content = (
            <DateTimePicker
                autoFocus={false}
                time={false}
                noTimezone={true}
                onChange={(value) => handleChange(value.toString())}
                className={styles.DatePicker}
                value={value}
                max={new Date(2099, 11, 31)}
                id={`${props.label}_date_selector`}
                name='date-selector'
            />
        );
    } else if (value) {
        content = formatDate(new Date(value));
    }

    return (
        <div style={{ paddingTop: '10px' }}>
            <p className={styles.InputLabel}>
                {label}{' '}
                {isRequired && (<span className={styles.required}>*Required{' '}</span>)}
                {Tooltip && (<Tooltip />)}
            </p>
            {content}
        </div>
    );
}

export default connect(
    (state, props) => {
        const institutionId = props.institutionId || get(state, 'me.institution.id');

        return {
            dataSources: filter(state.dataSources, ['institutionId', institutionId]),
        };
    },
    {
        loadDataSourceList,
        loadDataSource,
        saveDataSource,
        writeDataSourceValues,
        loadDataSourceLog
    }
)(BinderV4Field);